import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Cinematic hover card that combines three effects:
 *  1. Lift  — card rises + scales slightly on hover (whileHover translateY + scale)
 *  2. Glow  — large soft gold drop-shadow appears on hover (via className)
 *  3. Tilt  — 3D rotation follows the cursor (rotateX / rotateY with spring)
 *
 * Wrap any card content:
 *   <TiltCard><div>...</div></TiltCard>
 *
 * Props:
 *  - max: maximum rotation in degrees (default 5, keep subtle for spa)
 *  - lift: pixels to translateY on hover (default 6)
 *  - glow: css box-shadow applied on hover
 */
export const TiltCard = ({
  children,
  max = 5,
  lift = 6,
  className = "",
  testid,
  glow = "0 22px 60px -18px rgba(197,160,89,0.45)",
}) => {
  const ref = useRef(null);
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const spx = useSpring(px, { stiffness: 180, damping: 22, mass: 0.5 });
  const spy = useSpring(py, { stiffness: 180, damping: 22, mass: 0.5 });
  // Map 0..1 pointer position to -max..+max degrees (invert Y for natural tilt)
  const rotateY = useTransform(spx, [0, 1], [-max, max]);
  const rotateX = useTransform(spy, [0, 1], [max, -max]);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    px.set((e.clientX - rect.left) / rect.width);
    py.set((e.clientY - rect.top) / rect.height);
  };
  const onLeave = () => {
    px.set(0.5);
    py.set(0.5);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={testid}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        transformPerspective: 900,
      }}
      whileHover={{
        y: -lift,
        scale: 1.015,
        boxShadow: glow,
        transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
      }}
      className={`tilt-card group/tilt ${className}`}
    >
      {children}
    </motion.div>
  );
};
